/**
 * Explorer Panel - Multi-Artifact Project Browser
 *
 * Displays projects organized by status (WORKBENCH vs LIBRARY)
 * with artifacts grouped by type (SKILL, SUBFLOW, INTEGRATION, ATTRIBUTE)
 */

import { useState, useEffect, useRef } from 'react';
import { ComponentContainer } from 'golden-layout';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tooltip } from '../ui/Tooltip';
import { InputModal } from '../modals/InputModal';
import { ProjectFormModal } from '../modals/ProjectFormModal';
import { DeleteProjectModal } from '../modals/DeleteProjectModal';
import { ChangeSuffixModal } from '../modals/ChangeSuffixModal';
import { API_BASE_URL } from '@/lib/config';
import {
  Plus,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FolderPlus,
  File,
  GitBranch,
  Plug,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Circle,
  MessageSquare,
  FileText,
  Code,
  Palette,
  Workflow,
  Download,
  Trash2,
  GripVertical,
  Library,
  Wrench,
  Unplug,
  Copy,
  Search,
  X,
  Layers,
  Braces,
  Loader2,
  DraftingCompass
} from 'lucide-react';
import { useProjectStore, type ArtifactStageType, type ArtifactType, type Project as StoreProject, type ProjectArtifact, type ProjectStageType, type StageType } from '@/stores/projectStore';
import { cn } from '@/lib/utils';
import { useUserStore, buildUserHeaders } from '@/stores/userStore';

// Persist last loaded user across unmounts of this component (MainLayout rebuilds)
let lastLoadedUserGlobal: string | null = null;

// Artifact type configuration
const ARTIFACT_TYPE_CONFIG = {
  SKILL: {
    icon: File,
    label: 'Skills',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    defaultExpanded: true
  },
  SUBSKILL: {
    icon: FileText,
    label: 'Subskills',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    defaultExpanded: true
  },
  SUBFLOW: {
    icon: GitBranch,
    label: 'Subflows',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    defaultExpanded: true
  },
  INTEGRATION: {
    icon: Plug,
    label: 'Integrations',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    defaultExpanded: true
  },
  ATTRIBUTE: {
    icon: Database,
    label: 'Attributes',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    defaultExpanded: true
  },
  FACET: {
    icon: Unplug,
    label: 'Facets',
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    defaultExpanded: true
  }
};

// Stage configuration
const STAGE_CONFIG: Record<StageType, { icon: typeof MessageSquare; label: string }> = {
  PROMPT: { icon: MessageSquare, label: 'Prompt' }, // Will be made contextual dynamically
  PLAN: { icon: FileText, label: 'Plan' },
  ARTIFACT_PLAN: { icon: FileText, label: 'Artifact Plan' },
  LUMASCRIPT: { icon: Code, label: 'LumaScript' },
  BPMN: { icon: Workflow, label: 'Preview' },
  ARTIFACT_DESIGN: { icon: Layers, label: 'Artifact Design' },
  LUMA_SKILL: { icon: Braces, label: 'LumaJSON' }, // Final JSON output for Luma (legacy)
  LUMAJSON: { icon: Braces, label: 'LumaJSON' }
};

// Helper function to get contextual stage label
const getStageLabel = (stageName: StageType, artifactType: ArtifactType): string => {
  if (stageName === 'PROMPT') {
    // Make PROMPT label contextual based on artifact type
    switch (artifactType) {
      case 'SKILL':
        return 'Skill Prompt';
      case 'SUBSKILL':
        return 'Subskill Prompt';
      case 'SUBFLOW':
        return 'Subflow Prompt';
      case 'ATTRIBUTE':
        return 'Attribute Prompt';
      case 'INTEGRATION':
        return 'Integration Prompt';
      case 'FACET':
        return 'Facet Prompt';
      default:
        return 'Prompt';
    }
  }
  if (stageName === 'ARTIFACT_PLAN') {
    if (artifactType === 'ATTRIBUTE') return 'Attribute Plan';
    if (artifactType === 'INTEGRATION') return 'Integration Plan';
    return 'Artifact Plan';
  }
  // For all other stages, use the default label
  return STAGE_CONFIG[stageName].label;
};

interface ExplorerPanelProps {
  container?: ComponentContainer;
}

export const ExplorerPanel = ({ }: ExplorerPanelProps) => {
  const {
    projects,
    libraryProjects,
    activeProjectId,
    ui,
    loadAllProjects,
    loadProject,
    createProject,
    createArtifact,
    deleteArtifact,
    setActiveProject,
    setSelectedArtifact,
    setActiveStage,
    resetStore
  } = useProjectStore();
  const copyToWorkbench = useProjectStore((state) => state.copyToWorkbench);

  const [loadingProjects, setLoadingProjects] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Map<string, Set<ArtifactType>>>(new Map());
  const [showAllSections, setShowAllSections] = useState<Map<string, Set<ArtifactType>>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [copyingProjectId, setCopyingProjectId] = useState<string | null>(null);

  // Modal state
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewArtifactModal, setShowNewArtifactModal] = useState(false);
  const [newArtifactContext, setNewArtifactContext] = useState<{ projectId: string; type: ArtifactType } | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<StoreProject | null>(null);
  const [projectForSuffix, setProjectForSuffix] = useState<StoreProject | null>(null);
  const activeUserId = useUserStore((state) => state.activeUserId);
  const lastLoadedUserRef = useRef<string | null>(lastLoadedUserGlobal);

  // Get list of all project IDs
  const projectIds = Object.keys(projects);
  const projectList = projectIds.map(id => projects[id]).filter(Boolean);
  const libraryProjectList = Object.values(libraryProjects || {});

  // Filter projects and artifacts based on search query
  const filterProject = (project: StoreProject): boolean => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();

    // Match project name or suffix
    if (project.name.toLowerCase().includes(query) || project.suffix.toLowerCase().includes(query)) {
      return true;
    }

    // Match any artifact name or identifier
    const hasMatchingArtifact = Object.values(project.artifacts || {}).some(artifact =>
      artifact.name.toLowerCase().includes(query) ||
      artifact.identifier.toLowerCase().includes(query) ||
      artifact.fullIdentifier.toLowerCase().includes(query)
    );

    return hasMatchingArtifact;
  };

  // Separate by status and filter
  const workbenchProjects = projectList
    .filter(p => p.status === 'WORKBENCH' && filterProject(p))
    .sort((a, b) => {
      // Always put Inbox (Drafts) first
      if (a.isInbox) return -1;
      if (b.isInbox) return 1;
      // Sort others alphabetically
      return a.name.localeCompare(b.name);
    });
  const sharedLibraryProjects = libraryProjectList.filter(filterProject);

  // Load projects whenever active user changes (persist user across unmounts)
  useEffect(() => {
    if (!activeUserId) {
      resetStore();
      lastLoadedUserRef.current = null;
      lastLoadedUserGlobal = null;
      return;
    }

    const alreadyLoadedForUser =
      lastLoadedUserRef.current === activeUserId &&
      Object.keys(projects).length > 0;

    if (alreadyLoadedForUser) {
      return;
    }

    let cancelled = false;
    const loadProjects = async () => {
      setLoadingProjects(true);
      
      // NOTE: Removed aggressive resetStore() here to prevent "flash and wipe" on refresh.
      // loadAllProjects() will replace the state with fresh data for the current user anyway.
      
      try {
        await loadAllProjects();
        if (!cancelled) {
          lastLoadedUserRef.current = activeUserId;
          lastLoadedUserGlobal = activeUserId;
        }
      } catch (error) {
        console.error('[ExplorerPanel] Failed to load projects:', error);
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };

    // Defer to next tick to avoid synchronous setState loops
    const timer = setTimeout(loadProjects, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeUserId, loadAllProjects, resetStore]);

  // Auto-expand active project on mount (critical for when MainLayout remounts this component)
  useEffect(() => {
    if (projectIds.length > 0 && expandedProjects.size === 0) {
      // Prioritize active project, fallback to first project
      const targetProject = activeProjectId ? projects[activeProjectId] : workbenchProjects[0];

      if (targetProject) {
        setExpandedProjects(new Set([targetProject.id]));

        // Auto-expand all artifact sections
        const sections = new Map<string, Set<ArtifactType>>();
        sections.set(targetProject.id, new Set(['SKILL', 'SUBSKILL', 'SUBFLOW', 'INTEGRATION', 'ATTRIBUTE', 'FACET']));
        setExpandedSections(sections);

        // Load artifacts if not already loaded
        if (!targetProject.artifactsLoaded) {
          loadProject(targetProject.id).catch(error => {
            console.error('[ExplorerPanel] Failed to load project artifacts:', error);
          });
        }
      }
    }
  }, [projectIds.length, activeProjectId]);

  const handleNewProject = () => {
    setShowNewProjectModal(true);
  };

  const handleCopyProject = async (project: StoreProject) => {
    setCopyingProjectId(project.id);
    try {
      const newProjectId = await copyToWorkbench(project.id);
      await loadAllProjects();
      setExpandedProjects((prev) => new Set(prev).add(newProjectId));
    } catch (error) {
      console.error('[ExplorerPanel] Failed to copy project:', error);
      alert('Failed to copy project. Please try again.');
    } finally {
      setCopyingProjectId(null);
    }
  };

  const handleCreateProject = async (data: { name: string; description?: string; agentPrompt?: string }) => {
    setShowNewProjectModal(false);

    try {
      const projectId = await createProject(data.name, data.description, data.agentPrompt);
      // Default to agent prompt for a fresh project
      setActiveStage('PROMPT');

      // Auto-expand new project
      setExpandedProjects(prev => new Set(prev).add(projectId));
      setExpandedSections(prev => {
        const newMap = new Map(prev);
        newMap.set(projectId, new Set(['SKILL', 'SUBSKILL', 'SUBFLOW', 'INTEGRATION', 'ATTRIBUTE', 'FACET']));
        return newMap;
      });
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
    }
  };

  const handleProjectClick = async (projectId: string) => {
    const wasActiveProject = activeProjectId === projectId;
    // Load project details (artifacts and stages) if not already loaded
    const projectLoaded = projects[projectId]?.artifactsLoaded && projects[projectId]?.stagesLoaded;
    
    if (!projectLoaded) {
      try {
        await loadProject(projectId);
      } catch (error) {
        console.error('Failed to load project:', error);
        return;
      }
    }

    setActiveProject(projectId);

    // If we're switching projects (or coming from an artifact), choose a sensible stage
    if (!wasActiveProject || ui.selectedArtifactId) {
      const currentProject = useProjectStore.getState().projects[projectId] || projects[projectId];
      const bestStage = getDefaultProjectStage(currentProject);
      setActiveStage(bestStage);
    }

    // Auto-expand
    setExpandedProjects(prev => new Set(prev).add(projectId));
    setExpandedSections(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(projectId)) {
        newMap.set(projectId, new Set(['SKILL', 'SUBSKILL', 'SUBFLOW', 'INTEGRATION', 'ATTRIBUTE', 'FACET']));
      }
      return newMap;
    });
  };

  const toggleProjectExpanded = async (projectId: string) => {
    const isExpanding = !expandedProjects.has(projectId);

    // Load project if expanding and not loaded
    if (isExpanding && !projects[projectId]?.artifactsLoaded) {
      try {
        await loadProject(projectId);
      } catch (error) {
        console.error('Failed to load project:', error);
        return;
      }
    }

    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });

    // Auto-expand Skills when expanding a project
    if (isExpanding) {
      setExpandedSections(prev => {
        const newMap = new Map(prev);
        const sections = newMap.get(projectId) || new Set<ArtifactType>();
        sections.add('SKILL');
        newMap.set(projectId, sections);
        return newMap;
      });
    }
  };

  const toggleSection = (projectId: string, type: ArtifactType) => {
    setExpandedSections(prev => {
      const newMap = new Map(prev);
      const sections = newMap.get(projectId) || new Set();

      if (sections.has(type)) {
        sections.delete(type);
      } else {
        sections.add(type);
      }

      newMap.set(projectId, sections);
      return newMap;
    });
  };

  const isSectionExpanded = (projectId: string, type: ArtifactType): boolean => {
    return expandedSections.get(projectId)?.has(type) || false;
  };

  const toggleShowAll = (projectId: string, type: ArtifactType) => {
    setShowAllSections(prev => {
      const newMap = new Map(prev);
      const sections = newMap.get(projectId) || new Set();

      if (sections.has(type)) {
        sections.delete(type);
      } else {
        sections.add(type);
      }

      newMap.set(projectId, sections);
      return newMap;
    });
  };

  const isShowingAll = (projectId: string, type: ArtifactType): boolean => {
    return showAllSections.get(projectId)?.has(type) || false;
  };

  const handleNewArtifact = (projectId: string, type: ArtifactType) => {
    setNewArtifactContext({ projectId, type });
    setShowNewArtifactModal(true);
  };

  const handleCreateArtifact = async (name: string) => {
    if (!newArtifactContext) return;

    const { projectId, type } = newArtifactContext;

    // Prevent FACET creation - feature not yet implemented
    if (type === 'FACET') {
      setShowNewArtifactModal(false);
      setNewArtifactContext(null);
      alert('Facets are not yet implemented. We\'re currently designing the best workflow for facets and determining whether they should be specialized subflows, subskills, or a unique artifact type. Check back in a future release!');
      return;
    }

    setShowNewArtifactModal(false);
    setNewArtifactContext(null);

    try {
      const identifier = name.toLowerCase().replace(/\s+/g, '_');
      const artifactId = await createArtifact(projectId, type, identifier, name);

      // Select the new artifact
      setSelectedArtifact(artifactId);
    } catch (error) {
      console.error('Failed to create artifact:', error);
      alert('Failed to create artifact. Please try again.');
    }
  };

  const handleCancelNewArtifact = () => {
    setShowNewArtifactModal(false);
    setNewArtifactContext(null);
  };

  const getStageSequence = (artifact: ProjectArtifact): StageType[] => {
    const hasLegacyFinalStage = Boolean(artifact.stages?.LUMA_SKILL);
    const hasLumaJsonData = artifact.lumaJson !== null && artifact.lumaJson !== undefined;
    const hasNewFinalStage = Boolean(artifact.stages?.LUMAJSON) || hasLumaJsonData;

    if (artifact.type === 'ATTRIBUTE' || artifact.type === 'INTEGRATION') {
      const sequence: StageType[] = ['PROMPT', 'ARTIFACT_PLAN'];
      if (hasNewFinalStage) {
        sequence.push('LUMAJSON');
      } else if (hasLegacyFinalStage) {
        sequence.push('LUMA_SKILL');
      } else {
        sequence.push('LUMAJSON');
      }
      return sequence;
    }

    const sequence: StageType[] = ['PROMPT', 'ARTIFACT_PLAN', 'LUMASCRIPT', 'BPMN', 'ARTIFACT_DESIGN'];
    if (hasLegacyFinalStage) {
      sequence.push('LUMA_SKILL');
    }
    if (hasNewFinalStage && !sequence.includes('LUMAJSON')) {
      sequence.push('LUMAJSON');
    }
    if (!hasLegacyFinalStage && !hasNewFinalStage) {
      sequence.push('LUMA_SKILL');
    }
    return sequence;
  };

  const getStagePriority = (artifact: ProjectArtifact): StageType[] => {
    if (artifact.type === 'ATTRIBUTE' || artifact.type === 'INTEGRATION') {
      return ['LUMAJSON', 'LUMA_SKILL', 'ARTIFACT_PLAN', 'PROMPT'];
    }
    return ['LUMAJSON', 'LUMA_SKILL', 'LUMASCRIPT', 'BPMN', 'ARTIFACT_PLAN', 'PROMPT'];
  };

const PROJECT_STAGE_SEQUENCE: ProjectStageType[] = ['PLAN', 'LUMASCRIPT', 'BPMN', 'ARTIFACT_DESIGN'];

const getDefaultProjectStage = (project?: StoreProject | null): StageType => {
  if (!project) return 'PROMPT';
  const stageOrder: ProjectStageType[] = ['ARTIFACT_DESIGN', 'BPMN', 'LUMASCRIPT', 'PLAN'];
  for (const stage of stageOrder) {
    const content = project.stages?.[stage]?.content?.trim();
    if (content) {
      return stage;
    }
  }
  return 'PROMPT';
};

  const renderProjectStageIndicators = (project: StoreProject) => {
    return (
      <div className="flex items-center gap-0.5 mr-1">
        {PROJECT_STAGE_SEQUENCE.map(stageName => {
          const stage = project.stages?.[stageName];
          const Icon = STAGE_CONFIG[stageName].icon;

          const hasContent = Boolean(stage?.content?.trim());
          const isDirty = Boolean(stage?.isDirty);
          const notStarted = !hasContent;
          const isComplete = hasContent && !isDirty;
          const statusText = notStarted ? 'Not Started' : isDirty ? 'Modified' : 'Complete';
          const tooltipContent = `${STAGE_CONFIG[stageName].label}: ${statusText}`;

          return (
            <Tooltip key={stageName} content={tooltipContent}>
              <Icon
                className={cn(
                  'w-3 h-3',
                  notStarted && 'text-gray-300 dark:text-gray-600',
                  isDirty && 'text-orange-500',
                  isComplete && 'text-green-500'
                )}
              />
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const handleArtifactClick = (projectId: string, artifactId: string) => {
    // Set active project if not already active
    if (activeProjectId !== projectId) {
      setActiveProject(projectId);
    }

    // Select the artifact
    setSelectedArtifact(artifactId);

    // Determine which tab to switch to based on artifact stage completion
    // Strategy: Show the most recent completed stage, or the first incomplete stage
    const project = projects[projectId];
    const artifact = project?.artifacts?.[artifactId];

    if (artifact?.stages) {
      const stages = artifact.stages;
      const promptStage = stages.PROMPT;
      const planStage = stages.ARTIFACT_PLAN;

      // If we only have a prompt template so far, keep the user on Prompt
      if (promptStage?.content?.trim() && !planStage?.content?.trim()) {
        setActiveStage('PROMPT');
        return;
      }

      const stagePriority = getStagePriority(artifact);
      for (const stage of stagePriority) {
        if (stage === 'LUMAJSON') {
          const lumaJsonStage = stages.LUMAJSON ?? stages.LUMA_SKILL;
          const hasLumaJsonContent =
            artifact.lumaJson !== null &&
            artifact.lumaJson !== undefined;
          if (hasLumaJsonContent || lumaJsonStage?.content?.trim()) {
            setActiveStage('LUMAJSON');
            return;
          }
          continue;
        }

        if (stage === 'LUMA_SKILL') {
          const lumaSkillStage = stages.LUMA_SKILL ?? stages.LUMAJSON;
          const hasLumaJsonContent =
            artifact.lumaJson !== null &&
            artifact.lumaJson !== undefined;
          if (hasLumaJsonContent || lumaSkillStage?.content?.trim()) {
            setActiveStage('LUMA_SKILL');
            return;
          }
          continue;
        }

        const stageData = stages[stage as ArtifactStageType];
        if (!stageData) continue;

        if (stage === 'PROMPT') {
          if (stageData.content?.trim()) {
            setActiveStage(stage);
            return;
          }
        } else if (stage === 'ARTIFACT_PLAN') {
          const hasPlanContent = stageData.content?.trim() || (artifact.type === 'ATTRIBUTE' || artifact.type === 'INTEGRATION' ? artifact.seedPlan?.trim() : '');
          if (hasPlanContent) {
            setActiveStage(stage);
            return;
          }
        } else if (stageData.content?.trim() || !stageData.isDirty) {
          setActiveStage(stage);
          return;
        }
      }
    }

    // Default to Prompt when we don't have meaningful content yet
    setActiveStage('PROMPT');
  };

  const handleDeleteArtifact = async (projectId: string, artifactId: string, artifactName: string) => {
    if (!confirm(`Are you sure you want to delete "${artifactName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteArtifact(projectId, artifactId);
    } catch (error) {
      console.error('Failed to delete artifact:', error);
      alert('Failed to delete artifact. Please try again.');
    }
  };

  const handleDuplicateArtifact = async (projectId: string, artifactId: string) => {
    // TODO: Implement duplicate functionality
    console.log('[ExplorerPanel] Duplicate artifact:', { projectId, artifactId });
    alert('Duplicate functionality coming soon!');
  };

  const handleExportArtifact = async (projectId: string, artifactId: string) => {
    // TODO: Implement export functionality
    console.log('[ExplorerPanel] Export artifact:', { projectId, artifactId });
    alert('Export functionality coming soon!');
  };

  const handleExportProject = async (project: StoreProject) => {
    try {
      console.log('[ExplorerPanel] Exporting project:', project.id);

      const response = await fetch(`${API_BASE_URL}/projects/${project.id}/export`, {
        method: 'POST',
        headers: buildUserHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export project');
      }

      // Get the ZIP blob
      const blob = await response.blob();

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `${project.name}_${project.suffix}.zip`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('[ExplorerPanel] Project exported successfully:', filename);
    } catch (error) {
      console.error('[ExplorerPanel] Failed to export project:', error);
      alert(`Failed to export project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteProjectClick = (project: StoreProject) => {
    // Prevent deleting Inbox
    if (project.isInbox) {
      alert('Cannot delete Inbox project');
      return;
    }
    setProjectToDelete(project);
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/projects/${projectToDelete.id}`, {
        method: 'DELETE',
        headers: buildUserHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to delete project');
      }

      // Clear active project if we're deleting it
      if (activeProjectId === projectToDelete.id) {
        setActiveProject(null);
        setSelectedArtifact(null);
      }

      // Reload projects
      await loadAllProjects();

      setProjectToDelete(null);
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const renderArtifactStageIndicators = (artifact: ProjectArtifact) => {
    const hasLumaJsonData = artifact.lumaJson !== null && artifact.lumaJson !== undefined;
    const stages = getStageSequence(artifact);

    return (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {stages.map(stageName => {
          // Use artifact-level stages with legacy/new final stage fallback
          const stage =
            stageName === 'LUMA_SKILL'
              ? artifact.stages?.LUMA_SKILL ?? artifact.stages?.LUMAJSON
              : stageName === 'LUMAJSON'
                ? artifact.stages?.LUMAJSON ?? artifact.stages?.LUMA_SKILL
                : artifact.stages?.[stageName];
          const Icon = STAGE_CONFIG[stageName].icon;

          // Determine stage status based on content
          let hasContent = false;
          let isDirty = false;

          // Special handling for LUMA_SKILL stage - check lumaJson field
          if (stageName === 'LUMA_SKILL' || stageName === 'LUMAJSON') {
            // Check if artifact has lumaJson data (this is the exported JSON)
            const stageContent = stage?.content?.trim() ?? '';
            hasContent = hasLumaJsonData || stageContent.length > 0;
            isDirty = false; // lumaJson is never "dirty" - it's either there or not
          } else if (stageName === 'ARTIFACT_PLAN' && (artifact.type === 'ATTRIBUTE' || artifact.type === 'INTEGRATION')) {
            const planContent = (stage?.content ?? '').trim() || artifact.seedPlan?.trim();
            hasContent = Boolean(planContent);
            isDirty = Boolean(stage?.isDirty);
          } else {
            hasContent = !!stage && stage.content && stage.content.trim().length > 0;
            isDirty = !!stage && stage.isDirty;
          }

          const notStarted = !hasContent;
          const isComplete = hasContent && !isDirty;

          const statusText = notStarted ? 'Not Started' : isDirty ? 'Modified' : 'Complete';
          const stageLabel = getStageLabel(stageName, artifact.type);
          const tooltipContent = `${stageLabel}: ${statusText}`;

          return (
            <Tooltip key={stageName} content={tooltipContent}>
              <Icon
                className={cn(
                  'w-3 h-3',
                  notStarted && 'text-gray-300 dark:text-gray-600',
                  isDirty && 'text-orange-500',
                  isComplete && 'text-green-500'
                )}
              />
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const renderValidationBadge = (artifact: ProjectArtifact) => {
    if (!artifact.validation) {
      return (
        <div className="flex items-center gap-1 text-gray-400">
          <Circle className="w-3 h-3" />
        </div>
      );
    }

    const { status, confidence } = artifact.validation;

    return (
      <div className="flex items-center gap-1">
        {status === 'VALID' && (
          <>
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span className="text-[10px] text-green-600 dark:text-green-400">{confidence}%</span>
          </>
        )}
        {status === 'INVALID' && (
          <>
            <XCircle className="w-3 h-3 text-red-500" />
            <span className="text-[10px] text-red-600 dark:text-red-400">{confidence}%</span>
          </>
        )}
        {status === 'WARNING' && (
          <>
            <AlertTriangle className="w-3 h-3 text-yellow-500" />
            <span className="text-[10px] text-yellow-600 dark:text-yellow-400">{confidence}%</span>
          </>
        )}
        {status === 'PENDING' && (
          <>
            <Circle className="w-3 h-3 text-blue-500 animate-pulse" />
            <span className="text-[10px] text-blue-600 dark:text-blue-400">...</span>
          </>
        )}
      </div>
    );
  };

  const renderArtifactRow = (projectId: string, artifact: ProjectArtifact) => {
    const isSelected = ui.selectedArtifactId === artifact.id;

    return (
      <div
        key={artifact.id}
        className={cn(
          'group flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors',
          isSelected
            ? 'bg-primary/20 text-primary'
            : 'hover:bg-accent'
        )}
        onClick={() => handleArtifactClick(projectId, artifact.id)}
      >
        {/* Drag Handle */}
        <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-30 transition-opacity flex-shrink-0" />

        {/* Stage Indicators (in tree indent space) */}
        {renderArtifactStageIndicators(artifact)}
        <span className="h-4 w-px bg-foreground/40 mx-1" />

        {/* Artifact Name */}
        <span className="text-xs flex-1 truncate">
          {artifact.name}
        </span>

        {/* Validation Badge */}
        {renderValidationBadge(artifact)}

        {/* Actions */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Tooltip content="Duplicate">
            <button
              className="p-0.5 hover:bg-accent rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateArtifact(projectId, artifact.id);
              }}
            >
              <Copy className="w-3 h-3 text-blue-500" />
            </button>
          </Tooltip>
          <Tooltip content="Export">
            <button
              className="p-0.5 hover:bg-accent rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleExportArtifact(projectId, artifact.id);
              }}
            >
              <Download className="w-3 h-3 text-green-500" />
            </button>
          </Tooltip>
          <Tooltip content="Delete">
            <button
              className="p-0.5 hover:bg-accent rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArtifact(projectId, artifact.id, artifact.name);
              }}
            >
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderArtifactSection = (project: StoreProject, type: ArtifactType) => {
    const config = ARTIFACT_TYPE_CONFIG[type];
    const Icon = config.icon;

    // Filter artifacts by type and search query
    let artifacts = Object.values(project.artifacts || {}).filter(a => a.type === type);

    // Apply search filter if query exists
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      artifacts = artifacts.filter(artifact =>
        artifact.name.toLowerCase().includes(query) ||
        artifact.identifier.toLowerCase().includes(query) ||
        artifact.fullIdentifier.toLowerCase().includes(query)
      );
    }

    const isExpanded = isSectionExpanded(project.id, type);
    const showingAll = isShowingAll(project.id, type);
    const displayArtifacts = showingAll ? artifacts : artifacts.slice(0, 5);
    const emptyLabel = `No ${config.label.toLowerCase()} yet`;

    return (
      <div key={type} className="mb-2">
        {/* Section Header */}
        <div className="flex items-center gap-1.5 px-2 py-1 hover:bg-accent/30 rounded cursor-pointer group">
          <button
            onClick={() => toggleSection(project.id, type)}
            className="p-0.5 hover:bg-accent rounded"
            disabled={artifacts.length === 0}
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
          <Icon className={cn('w-3.5 h-3.5', config.color)} />
          <span className="text-xs font-medium flex-1 flex items-center gap-1">
            {config.label}
            {artifacts.length === 0 && (
              <span className="text-[11px] text-muted-foreground italic">{emptyLabel}</span>
            )}
          </span>
          <span className="text-xs text-muted-foreground min-w-[1.5rem] text-right">
            {artifacts.length}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNewArtifact(project.id, type);
            }}
            className="p-0.5 hover:bg-accent rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title={`New ${config.label.slice(0, -1)}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Artifacts List */}
        {isExpanded && artifacts.length > 0 && (
          <div className="pl-6 mt-1 space-y-0.5">
            {displayArtifacts.map(artifact => renderArtifactRow(project.id, artifact))}

            {!showingAll && artifacts.length > 5 && (
              <button
                className="text-xs text-primary hover:underline px-2 py-1"
                onClick={() => toggleShowAll(project.id, type)}
              >
                Show all ({artifacts.length})
              </button>
            )}

            {showingAll && artifacts.length > 5 && (
              <button
                className="text-xs text-muted-foreground hover:underline px-2 py-1"
                onClick={() => toggleShowAll(project.id, type)}
              >
                Show less
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderProject = (project: StoreProject, showDivider = false) => {
    const isReadOnly = project.isReadOnly || project.status === 'LIBRARY';
    const isExpandable = !isReadOnly;
    const isExpanded = isExpandable && expandedProjects.has(project.id);
    const isActive = activeProjectId === project.id;
    const isCopying = copyingProjectId === project.id;
    const displayName = project.isInbox ? 'Drafts' : project.name;

    return (
      <div key={project.id} className="mb-2">
        {/* Project Header */}
        <div
          className={cn(
            'flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer group transition-colors',
            isActive ? 'bg-accent' : 'hover:bg-accent/50'
          )}
          onClick={() => {
            if (!isReadOnly) {
              handleProjectClick(project.id);
            }
          }}
        >
          {isExpandable ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleProjectExpanded(project.id);
            }}
            className="p-0.5 hover:bg-accent rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
          ) : (
            <div className="w-4 h-4" />
          )}
          {project.isInbox ? (
            <DraftingCompass className={cn('w-3.5 h-3.5', isActive && 'text-primary')} />
          ) : (
            <FolderOpen className={cn('w-3.5 h-3.5', isActive && 'text-primary')} />
          )}
          {!isReadOnly && (
            <>
              <span className="h-4 w-px bg-foreground/40 mx-1" />
              {renderProjectStageIndicators(project)}
              <span className="h-4 w-px bg-foreground/40 mx-1" />
            </>
          )}
          <span className="text-xs flex-1 truncate font-medium">
            {displayName}
          </span>

          {/* Suffix Badge */}
          <span className="h-4 w-px bg-foreground/40 mx-1" />
          <Badge
            variant="outline"
            size="sm"
            className={project.isInbox ? 'min-w-[3.25rem] text-center justify-center' : ''}
          >
            {project.suffix}
          </Badge>

          {/* Status Badge */}
          <Badge
            variant={project.status === 'LIBRARY' ? 'success' : 'warning'}
            size="sm"
          >
            {project.status}
          </Badge>
          {project.ownerName && (
            <Badge variant="outline" size="sm">
              {project.ownerName}
            </Badge>
          )}

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-0.5 hover:bg-accent rounded"
              title="Export Project"
              onClick={(e) => {
                e.stopPropagation();
                handleExportProject(project);
              }}
            >
              <Download className="w-3 h-3" />
            </button>
            {isReadOnly ? (
              <button
                className="p-0.5 hover:bg-accent rounded"
                title="Copy to Workbench"
                disabled={isCopying}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyProject(project);
                }}
              >
                {isCopying ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FolderPlus className="w-3 h-3" />
                )}
              </button>
            ) : (
              <>
                <button
                  className="p-0.5 hover:bg-accent rounded"
                  title="Change Project Suffix"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProjectForSuffix(project);
                  }}
                >
                  <Wrench className="w-3 h-3" />
                </button>
                {!project.isInbox && (
                  <button
                    className="p-0.5 hover:bg-red-100 dark:hover:bg-red-950/20 rounded text-muted-foreground hover:text-red-600"
                    title="Delete Project"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProjectClick(project);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Project Contents */}
        {isReadOnly && (
          <div className="pl-6 text-xs text-muted-foreground mt-1">
            Copy this project to your workbench to inspect or edit artifacts.
          </div>
        )}

        {isExpanded && !isReadOnly && (
          <div className="pl-4 mt-1 border-l-2 border-border ml-2">
            {renderArtifactSection(project, 'SKILL')}
            {renderArtifactSection(project, 'ATTRIBUTE')}
            {renderArtifactSection(project, 'SUBSKILL')}
            {renderArtifactSection(project, 'SUBFLOW')}
            {renderArtifactSection(project, 'FACET')}
            {renderArtifactSection(project, 'INTEGRATION')}
          </div>
        )}
        {showDivider && <div className="h-px bg-border/50 my-2 mx-2" />}
      </div>
    );
  };

  return (
    <div className="panel-container">
      <div className="panel-header">
        <h3 className="text-sm font-semibold">Explorer</h3>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleNewProject}
          title="New Project"
          className="ml-auto"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Search Bar */}
      <div className="px-2 py-1.5 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search projects and artifacts..."
            className="w-full pl-8 pr-7 py-1 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-accent rounded"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      <div className="panel-content p-2 overflow-y-auto overflow-x-hidden">
        {loadingProjects ? (
          <div className="text-xs text-muted-foreground pl-6">Loading...</div>
        ) : (
          <>
            {/* Library Section */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-2">
                <Library className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium">Library</span>
                <span className="text-xs text-muted-foreground">
                  ({sharedLibraryProjects.length})
                </span>
              </div>
              {sharedLibraryProjects.length === 0 ? (
                <div className="text-xs text-muted-foreground pl-6">
                  No published projects yet
                </div>
              ) : (
                <div className="space-y-1 pl-2">
                  {sharedLibraryProjects.map(p => renderProject(p))}
                </div>
              )}
            </div>

            {/* Workbench Section */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-2">
                <Wrench className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Workbench</span>
                <span className="text-xs text-muted-foreground">
                  ({workbenchProjects.length})
                </span>
              </div>
              {workbenchProjects.length === 0 ? (
                <div className="text-xs text-muted-foreground pl-6">
                  No projects yet. Create one to get started.
                </div>
              ) : (
                <div className="space-y-1 pl-2">
                  {workbenchProjects.map((p, i) => renderProject(p, p.isInbox && i < workbenchProjects.length - 1))}
                </div>
              )}
            </div>

            {/* Help Text */}
            {projectList.length === 0 && (
              <div className="text-xs text-muted-foreground p-4 border border-dashed border-border rounded">
                <p className="font-medium mb-2">Getting Started</p>
                <p className="mb-2">
                  Create a project to organize your skills, subflows, integrations, and attributes.
                </p>
                <p>
                  Everything exists within a project - there are no standalone resources.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Project Modal */}
      <ProjectFormModal
        isOpen={showNewProjectModal}
        title="New Project"
        onSave={handleCreateProject}
        onCancel={() => setShowNewProjectModal(false)}
      />

      {/* New Artifact Modal */}
      {newArtifactContext && (
        <InputModal
          isOpen={showNewArtifactModal}
          title={`New ${ARTIFACT_TYPE_CONFIG[newArtifactContext.type].label.slice(0, -1)}`}
          label={`${ARTIFACT_TYPE_CONFIG[newArtifactContext.type].label.slice(0, -1)} Name`}
          placeholder={`e.g., ${newArtifactContext.type === 'SKILL' ? 'Password Reset' : newArtifactContext.type === 'INTEGRATION' ? 'Exchange Online' : 'User Email'}`}
          onSave={handleCreateArtifact}
          onCancel={handleCancelNewArtifact}
        />
      )}

      {/* Delete Project Modal */}
      {projectToDelete && (
        <DeleteProjectModal
          project={projectToDelete}
          onClose={() => setProjectToDelete(null)}
          onConfirm={handleConfirmDeleteProject}
        />
      )}
      {projectForSuffix && (
        <ChangeSuffixModal
          projectId={projectForSuffix.id}
          projectName={projectForSuffix.name}
          onClose={() => setProjectForSuffix(null)}
        />
      )}
    </div>
  );
};
